(ns manage.content-configuration-page.context-source.shared
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            [common.ui.entity :refer [query-param->config]]
            [common.ui.store :as store]
            [manage.content-configuration-page.context-source.schema
             :refer
             [schema-id->icon]]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$]]))

(defmulti parameter
  "Executed when exporting a context source as context in a content function,
  to calculate the parameter in the payload."
  :context-source/schema)

(defmethod parameter :default
  [_] nil)

(defmulti original-parameter
  "Executed when exporting a context source as context in a content function,
  to return the original parameter (as imported)."
  :context-source/schema)

(defmethod original-parameter :default
  [{:context-source/keys [original-parameter]}]
  original-parameter)

(defmulti context->constructor
  "Executed when importing a context from a content function, typically to support
  special handling of the `param` value.

  When calling `context->constructor`, the argument must be a map representing a
  context, containing the `alias`, `schema`, and `param` keys."
  :schema)

(defmethod context->constructor :default
  [{:keys [schema] :as context}]
  (rf/dispatch
   [:sentry/capture
    [(if schema
       "Missing context->constructor implementation"
       "Cannot dispatch context->constructor")
     {:contexts {"Context source" (assoc context :imported-context (pr-str context))}}]]))

(defn context-source-dbentity->schema-id [ref]
  @(store/get-value ref :context-source/schema))

(defmulti entity-view-cfg
  "Context source specific rendering in the entity viewer component.

  When calling `entity-view-cfg`, the argument must be a reference to an
  entity in the datascript database, via DatascriptDBEntity"
  context-source-dbentity->schema-id)

(defn alias->entity-query-params [alias]
  {:entity :context-source :alias alias})

(rf/reg-sub :context-source/query-params
  (fn [[_ context-source _new-context-source-alias]]
    (store/get-value context-source :context-source/alias))
  (fn [context-source-alias [_ _context-source new-context-source-alias]]
    (alias->entity-query-params (or new-context-source-alias context-source-alias))))

(defn schema-label [schema-id]
  (get @(rf/subscribe [:resource.schema/meta-by-id schema-id]) :label))

(defn entity-view-cfg-base [cs]
  (let [schema-id (context-source-dbentity->schema-id cs)]
    {:entity/ref cs
     :entity/label :context-source/alias
     :entity/icon (schema-id->icon schema-id)
     :entity/read-only? true
     :validation/schema :context-source
     :list-item/allow-delete-item? true
     :list-item/details [schema-label schema-id]}))

(defmethod entity-view-cfg :default
  [cs]
  (entity-view-cfg-base cs))

(defmulti entity-query-params
  "Some context sources require additional configuration before
   they can be used in targeting. This is usually the case when
   the schema also has its own defmethod for `entity-view-cfg`.

   When additional configuration is needed, this defmulti returns
   the query-params that can be used to route the user to the
   entity view where the context source can be configured."
  :schema-id)

(defmethod entity-query-params :default
  []
  nil)

(def pull-pattern-context-source
  '[*
    {:dsp-signal-context-source/signal
     [:db/id :dsp-signal/name :dsp-signal/dsp-macro]}
    :products-context-source/product-selector-id
    :feed-context-source/feed-id
    {:feed-context-source/lookup
     [:feed-context-source-lookup/id
      :db/id
      {:feed-context-source-lookup/macro
       [:macro/selector
        {:context-source/_macro [:context-source/alias]}]}]}
    :poi-context-source/selector-id
    :poi-context-source/max-radius-km])

(defn configure-button [cs icon label]
  [:> Button
   {:href @(rf/subscribe [:entity/link @(rf/subscribe [:context-source/query-params cs])])
    :start-icon ($ Icon icon)
    :variant "text"}
   label])

(defmethod query-param->config :context-source
  [{alias :alias}]
  (some-> @(rf/subscribe [:ui.store/lookup :context-source/alias alias])
          first store/->DataScriptDBEntity entity-view-cfg))
